/**
 * Honest Scorecard vs Competitors — public marketing weapon.
 *
 * Brutal-honest feature-by-feature matrix vs 13 FinOps incumbents. Every row
 * links to a live CARTIE page so HN engineers can verify in 1 click. Honest
 * where we don't have it: marked "◐ partial" or "Q2 2026" — no fake checkmarks.
 *
 * This page IS the moat — surfacing every differentiator we built, side by
 * side with what Vantage / Apptio / CloudHealth / CloudZero / Kubecost /
 * Helicone / Datadog / Spot.io / IBM Turbonomic / CAST AI / TrueFoundry /
 * Amnic / Mavvrik actually ship today (verified Jul 2026).
 */
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import {
  Check, X, CircleDot, Clock, ExternalLink, Trophy, Skull, Brain,
  Sparkles, Layers, Cpu, AlertOctagon, ArrowRight, ShieldCheck,
} from 'lucide-react';
// v34.55 — promoted from BackToHomePill → PublicNavbar so visitors get the same top nav as /pilot, /wall-of-tenants, etc.
import PublicNavbar from '../components/PublicNavbar';
import ScorecardShareButton from '../components/ScorecardShareButton';
import { COMPETITORS as VS_VENDORS } from '../data/competitorData';

// ─── Honest data — verified Jul 2026 ─────────────────────────────────────
//
// Verification sources used:
//   - Vantage: docs.vantage.sh + public pricing page
//   - Apptio Cloudability: ibm.com/products/cloudability + Apptio docs
//   - CloudHealth (VMware): docs.vmware.com/en/VMware-CloudHealth
//   - CloudZero: docs.cloudzero.com + their TaaS pricing
//   - Kubecost: kubecost.com/products/cost-allocation + GitHub README
//   - Helicone: docs.helicone.ai + their LLM observability product
//   - Datadog: docs.datadoghq.com (Cloud Cost Mgmt + LLM Observability)
//   - Spot.io (NetApp): spot.io/products (Eco · Ocean · Elastigroup)
//   - IBM Turbonomic: ibm.com/products/turbonomic (ARM platform)
//
// Status icons:
//   ✓  full = native, shipping, documented
//   ◐  partial = exists but limited (gateway, beta, paid add-on, narrow scope)
//   ✗  missing = not in product, not announced
//   ⏱  q2_2026 = honest "we don't have it yet, ETA Q2"
const STATUS = {
  full:    { icon: Check,      cls: 'text-emerald-300', label: 'Yes' },
  partial: { icon: CircleDot,  cls: 'text-amber-300',   label: 'Partial' },
  missing: { icon: X,           cls: 'text-rose-400/60', label: 'No' },
  q2_2026: { icon: Clock,       cls: 'text-emerald-300/80', label: 'Q3 2026' },
};

const COMPETITORS = [
  { id: 'cartie',      label: 'CARTIE',      accent: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200' },
  { id: 'vantage',     label: 'Vantage',     accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'apptio',      label: 'Apptio',      accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'cloudhealth', label: 'CloudHealth', accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'cloudzero',   label: 'CloudZero',   accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'kubecost',    label: 'Kubecost',    accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'helicone',    label: 'Helicone',    accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'datadog',     label: 'Datadog',     accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'spotio',      label: 'Spot.io',     accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'turbonomic',  label: 'Turbonomic',  accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'castai',      label: 'CAST AI',     accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'truefoundry', label: 'TrueFoundry', accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'amnic',       label: 'Amnic',       accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
  { id: 'mavvrik',     label: 'Mavvrik',     accent: 'bg-slate-800/60 border-slate-700 text-slate-300' },
];

const CATEGORIES = [
  {
    id: 'agents',
    label: 'FinOps for AI Agents',
    icon: Brain,
    blurb: 'Multi-step autonomous agents are the 2026 spend category nobody else measures.',
    rows: [
      {
        feature: 'Per-agent-run cost rollup',
        link: '/admin/agent-economics',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'partial', datadog: 'missing', spotio: 'missing', turbonomic: 'missing', truefoundry: 'partial', mavvrik: 'partial' },
        note: 'Helicone has session view but no agent_run_id hierarchy with parent_step_index. TrueFoundry traces agents at the gateway; Mavvrik ships an OTel agent SDK — neither rolls up per-run cost hierarchies.',
      },
      {
        feature: 'Runaway hard-cap pre-check API',
        link: '/admin/agent-economics',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing', truefoundry: 'partial', mavvrik: 'partial' },
        note: 'Agent calls /check-budget BEFORE each step; we recommend kill if over hard_cap. TrueFoundry enforces gateway budgets/rate limits; Mavvrik ships budget guardrails — neither does per-step pre-check.',
      },
      {
        feature: 'Per-run ROI (outcome ↔ cost)',
        link: '/admin/agent-economics',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'Finish a run with outcome_value_usd → roi_multiplier verdict.',
      },
      {
        feature: 'Rolling-median runaway detection',
        link: '/admin/agent-economics',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'Flags runs with cost or depth ≥ 2× rolling-7d median for the same agent name.',
      },
    ],
  },
  {
    id: 'invisible_waste',
    label: 'Invisible Waste Detection',
    icon: Skull,
    blurb: 'The things teams pay for and forget — zombie pods, cold embeddings, retried tokens, bloat prompts.',
    rows: [
      {
        feature: 'Orphaned Kubernetes services (zombie detector)',
        link: '/admin/zombie-hunter',
        scores: { cartie: 'full', vantage: 'partial', apptio: 'partial', cloudhealth: 'partial', cloudzero: 'partial', kubecost: 'full', helicone: 'missing', datadog: 'partial', spotio: 'full', turbonomic: 'partial', castai: 'partial', amnic: 'partial' },
        note: 'Kubecost is full-feature parity; we layer an engineer-hour payback gate they lack. CAST AI removes empty nodes (infra-level); Amnic surfaces K8s idle via rightsizing recs.',
      },
      {
        feature: 'Unnecessary LLM retry cost audit',
        link: '/token-intelligence',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'partial', datadog: 'partial', spotio: 'missing', turbonomic: 'missing', truefoundry: 'partial' },
        note: 'Helicone/TrueFoundry surface retry logs but dont quantify $ wasted.',
      },
      {
        feature: 'Over-context prompt optimizer',
        link: '/token-intelligence',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'Compresses bloated system prompts 50-75% — input-token reduction every call.',
      },
      {
        feature: 'Embedding-store cold-namespace audit',
        link: '/admin/embedding-audit',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'Pinecone/pgvector graveyard detection — nobody else even crawls this.',
      },
      {
        feature: 'Idle AI endpoint detector (cold budgets)',
        link: '/admin/idle-endpoints',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'Features with configured budgets but zero traffic in N days = forgotten deployments.',
      },
      {
        feature: 'Duplicated pipeline / SaaS overlap detection',
        link: '/admin/saas-spend',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'partial', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing', amnic: 'partial', mavvrik: 'partial' },
        note: 'Apptio has SaaS module but doesnt join SaaS ↔ Cloud (Datadog vs CloudWatch). Amnic/Mavvrik track SaaS spend alongside cloud — no overlap detection.',
      },
      {
        feature: 'Okta SCIM SaaS auto-discovery',
        link: '/admin/saas-spend',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'partial', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'Connect Okta once → CARTIE auto-imports every SaaS app + cross-references against existing subscriptions for overlap.',
      },
      {
        feature: 'Airflow / Dagster pipeline DAG dedup (Jaccard + lineage)',
        link: '/admin/pipeline-dedup',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'WORLD-FIRST. Detects customer_etl_v1/v2/FINAL-style duplicate pipelines via task-shape AND data-lineage Jaccard.',
      },
      {
        feature: 'Real-time LLM token arbitrage routing (Auto-Compiler Proxy)',
        link: '/engine-27',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'partial', datadog: 'missing', spotio: 'missing', turbonomic: 'missing', castai: 'partial', truefoundry: 'partial' },
        note: 'Live pricing index across 12 LLM providers + cheapest-quality-adjusted routing per task class. Helicone/TrueFoundry route by cost or latency; CAST AI\'s AI Enabler routes to cheap instances — none do live spot-burst detection or quality-floor scoring.',
      },
      {
        feature: 'Output-side verbosity waste audit (response diet)',
        link: '/output-diet',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'WORLD-FIRST. Output tokens cost 3-5× input — every other tool optimizes prompts only. We audit and shrink the response side: boilerplate tax, long-tail verbosity, prose-wrapped JSON.',
      },
      {
        feature: 'Multi-turn history resend tax analysis (memory compaction)',
        link: '/memory-compactor',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'WORLD-FIRST. Chat-history resend grows O(N²) per conversation. We quantify the tax from real proxy logs and prescribe the keep-last-N + rolling-summary policy that kills 40-70% of it.',
      },
      {
        feature: 'Cost SLOs with burn-rate math + projected exhaustion date',
        link: '/cost-slo',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'partial', kubecost: 'missing', helicone: 'missing', datadog: 'partial', spotio: 'missing', turbonomic: 'missing', amnic: 'partial', mavvrik: 'partial' },
        note: 'WORLD-FIRST framing. Datadog/CloudZero/Amnic/Mavvrik ship threshold budget alerts (fire after the money is gone). We ship SRE error-budget burn rates: 1.5× burn flags weeks before breach, with the projected exhaustion day.',
      },
      {
        feature: 'AI capacity commitment math (PTU / provisioned-throughput ROI)',
        link: '/ptu-planner',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'WORLD-FIRST. The RI-optimizer play for AI: should you buy Azure OpenAI PTUs / Bedrock provisioned throughput or stay PAYG? Deterministic verdict with every price assumption disclosed and overridable.',
      },
    ],
  },
  {
    id: 'attribution',
    label: 'Multi-Tenant Cost Allocation',
    icon: Layers,
    blurb: 'Per-customer · per-team · per-feature attribution in shared infrastructure.',
    rows: [
      {
        feature: 'Per-customer P&L (Stripe-revenue join)',
        link: '/customer-pl',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'partial', cloudhealth: 'missing', cloudzero: 'full', kubecost: 'missing', helicone: 'missing', datadog: 'partial', spotio: 'missing', turbonomic: 'missing', amnic: 'partial', mavvrik: 'partial' },
        note: 'CloudZero ships Customer-Cost Intelligence — direct competitor here. Amnic (unit economics) and Mavvrik (cost-to-serve) attribute cost per customer but dont join Stripe revenue.',
      },
      {
        feature: 'Per-pod Shapley cost attribution (K8s)',
        link: '/k8s-cost',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'partial', helicone: 'missing', datadog: 'missing', spotio: 'partial', turbonomic: 'partial', castai: 'partial', amnic: 'partial' },
        note: 'Kubecost/CAST AI/Amnic use proportional or utilization splits; we use Castro-2009 Monte-Carlo Shapley — provably fair.',
      },
      {
        feature: 'Per-feature AI profitability (revenue ÷ cost)',
        link: '/ai-feature-profitability',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'partial', kubecost: 'missing', helicone: 'partial', datadog: 'missing', spotio: 'missing', turbonomic: 'missing', mavvrik: 'partial' },
        note: 'CloudZero/Helicone/Mavvrik show feature cost — none join to revenue for ROI.',
      },
      {
        feature: 'Total Cost of Feature (TCF) 4-source join',
        link: '/admin/saas-spend',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'Cloud + LLM + SaaS + engineer-hours = total feature cost. WORLD-FIRST.',
      },
    ],
  },
  {
    id: 'business_aware',
    label: 'Business-Aware Optimization',
    icon: Trophy,
    blurb: 'Optimize for profit and conversion — not raw cost reduction.',
    rows: [
      {
        feature: 'Cost ↔ Conversion event join (ROI per feature)',
        link: '/admin/conversion-roi',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'partial', kubecost: 'missing', helicone: 'missing', datadog: 'partial', spotio: 'missing', turbonomic: 'missing', mavvrik: 'partial' },
        note: 'CloudZero shows cost-per-customer; Mavvrik markets cost-to-outcomes; we go further with feature-level event join.',
      },
      {
        feature: 'PostHog / Mixpanel webhook signature verification',
        link: '/admin/conversion-roi',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'HMAC-SHA256 verification on every inbound webhook. Shipped Feb 18, 2026.',
      },
      {
        feature: 'Jira / Linear webhook adapter for roadmap auto-import',
        link: '/admin/roadmap-forecast',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'Label-based extraction (launch + finops-delta:* / cartie-customers:*) + HMAC verify + idempotency.',
      },
      {
        feature: 'Engineer-hour payback filter (3× ROI gate)',
        link: '/admin/payback-filter',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'Refuses to recommend below 3× first-year ROI. No vendor refuses recommendations.',
      },
      {
        feature: 'Roadmap-aware Monte Carlo forecast',
        link: '/admin/roadmap-forecast',
        scores: { cartie: 'full', vantage: 'partial', apptio: 'full', cloudhealth: 'partial', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'partial', amnic: 'partial' },
        note: 'Apptio has good forecasting; Amnic forecasts spend trends; ours injects Jira/Linear launch events into fan chart.',
      },
      {
        feature: 'PR-time cost guardrail (engineer-hour math)',
        link: '/admin/pr-cost-guardrail',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'WARN/BLOCK PRs on cost-to-value ratio. Infracost does $ deltas; we add value math.',
      },
    ],
  },
  {
    id: 'trust',
    label: 'Trust & Honest Marketing',
    icon: ShieldCheck,
    blurb: 'The "is this just marketing?" gap every FinOps tool struggles with.',
    rows: [
      {
        feature: '3× ROI Pledge (binding refund contract)',
        link: '/roi-pledge',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'Versioned, machine-readable, curl-able. No FinOps vendor has this.',
      },
      {
        feature: 'Public algorithm correctness manifest',
        link: '/algorithm-correctness',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'Live endpoint returns test count + axiom list. Any HN commenter can verify.',
      },
      {
        feature: 'Continuous commitment rebalancer (ledger)',
        link: '/commitment-rebalancer',
        scores: { cartie: 'full', vantage: 'partial', apptio: 'partial', cloudhealth: 'partial', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'partial', spotio: 'full', turbonomic: 'partial' },
        note: 'Vantage/Apptio recommend once; we watch 30/60/90d windows + ledger every rebalance.',
      },
      {
        feature: 'Decision Ledger — two futures per decision, human-in-loop, graded forecasts',
        link: '/decision-ledger',
        scores: { cartie: 'full', vantage: 'missing', apptio: 'missing', cloudhealth: 'missing', cloudzero: 'missing', kubecost: 'missing', helicone: 'missing', datadog: 'missing', spotio: 'missing', turbonomic: 'missing' },
        note: 'WORLD-FIRST. Every money decision = Choice A vs Choice B with projected P&L, human approval trail, and predicted-vs-actual accuracy grading. No tool on earth grades its own forecasts.',
      },
    ],
  },
  {
    id: 'planned',
    label: 'Honestly: What We Don\'t Have Yet',
    icon: AlertOctagon,
    blurb: 'Where we admit the gap — and tell you when it ships.',
    rows: [
      {
        feature: 'SOC 2 Type II certification (A-LIGN audit, public readiness matrix live)',
        link: '/soc2-readiness',
        scores: { cartie: 'q2_2026', vantage: 'full', apptio: 'full', cloudhealth: 'full', cloudzero: 'full', kubecost: 'partial', helicone: 'partial', datadog: 'full', spotio: 'full', turbonomic: 'full', castai: 'full', truefoundry: 'full', amnic: 'partial', mavvrik: 'partial' },
        note: 'Type I controls 100% implemented (21/21), Type II audit targeted September 30, 2026. Live readiness matrix at /soc2-readiness — we publish what we have and what is pending. CAST AI and TrueFoundry are certified; Amnic/Mavvrik do not publish audit status.',
      },
    ],
  },
];

// ─── Score Summary ────────────────────────────────────────────────────────
function computeStandings() {
  const stand = {};
  COMPETITORS.forEach(c => { stand[c.id] = { full: 0, partial: 0, missing: 0, q2_2026: 0 }; });
  CATEGORIES.forEach(cat => cat.rows.forEach(row => {
    COMPETITORS.forEach(c => {
      const s = row.scores[c.id] || 'missing';
      if (stand[c.id][s] !== undefined) stand[c.id][s]++;
    });
  }));
  return stand;
}

const Cell = ({ score }) => {
  const meta = STATUS[score] || STATUS.missing;
  const Icon = meta.icon;
  return (
    <div className="flex items-center justify-center" title={meta.label}>
      <Icon className={`w-5 h-5 ${meta.cls}`} />
    </div>
  );
};

export default function HonestScorecard() {
  const [activeCat, setActiveCat] = useState('all');
  const standings = computeStandings();
  const totalRows = CATEGORIES.reduce((s, c) => s + c.rows.length, 0);
  const visibleCats = activeCat === 'all' ? CATEGORIES : CATEGORIES.filter(c => c.id === activeCat);

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="honest-scorecard-page">
      <Helmet>
        <title>Honest Scorecard vs FinOps Competitors | CARTIEAI</title>
        <meta name="description" content="Brutal feature-by-feature comparison: CARTIE vs Vantage, Apptio, CloudHealth, CloudZero, Kubecost, Helicone, Datadog, Spot.io, IBM Turbonomic, CAST AI, TrueFoundry, Amnic, Mavvrik. We mark our own gaps honestly." />
      </Helmet>
      <PublicNavbar />

      {/* Hero — premium dark theme with subtle brand atmosphere */}
      <section className="relative px-6 pt-24 pb-12 max-w-7xl mx-auto overflow-hidden">
        {/* Subtle emerald-only orbs for depth (decorative, behind content) */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-500/[0.06] blur-[120px]" />
          <div className="absolute top-32 right-1/4 w-[380px] h-[380px] rounded-full bg-emerald-500/[0.06] blur-[120px]" />
        </div>

        <div className="text-center mb-12 relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-emerald-300 mb-6 text-[11px] font-mono uppercase tracking-[0.18em] shadow-lg shadow-slate-900/20 border border-emerald-500/30">
            <ShieldCheck className="w-3.5 h-3.5" />
            Brutally Honest · Jul 2026
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold mb-6 tracking-tight leading-[1.05] text-white">
            The{' '}
            <span className="text-emerald-400">honest</span>
            <br className="sm:hidden" />
            <span> scorecard</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed font-medium">
            Feature-by-feature, with sources cited, gaps admitted.
          </p>
          <p className="text-base text-slate-400 max-w-3xl mx-auto leading-relaxed mt-2">
            Every <Check className="w-4 h-4 inline text-emerald-600 -mt-0.5" /> below links to a <span className="text-emerald-300 font-semibold">live page</span> you can hit right now. No <span className="line-through text-slate-400">"contact sales"</span> walls.
          </p>
          <div className="inline-flex items-center gap-4 mt-6 px-5 py-2 rounded-full bg-slate-900/60 border border-white/10 shadow-sm">
            <span className="text-sm tabular-nums font-mono text-slate-300"><span className="font-bold text-white">{totalRows}</span> rows</span>
            <span className="text-slate-400">·</span>
            <span className="text-sm tabular-nums font-mono text-slate-300"><span className="font-bold text-white">{COMPETITORS.length}</span> vendors</span>
            <span className="text-slate-400">·</span>
            <span className="text-sm font-mono text-slate-300">verified against <span className="font-semibold text-white">2026</span> product pages</span>
          </div>
        </div>

        {/* Standings */}
        <div className="rounded-2xl bg-slate-900/60 border border-white/10 overflow-hidden mb-8">
          <div className="px-6 py-3 border-b border-white/10 bg-slate-900/40">
            <div className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Standings — full coverage rows</div>
          </div>
          <div className="overflow-x-auto">
            <div className="grid divide-x divide-white/10 min-w-[1260px]" style={{ gridTemplateColumns: `repeat(${COMPETITORS.length}, minmax(0, 1fr))` }}>
              {COMPETITORS.map(c => (
                <div key={c.id} className={`px-2 pt-7 pb-5 text-center relative transition-all ${c.id === 'cartie' ? 'bg-emerald-500/10' : 'bg-slate-900/60'}`}>
                  {c.id === 'cartie' && (
                    <>
                      <div className="absolute top-0 left-0 right-0 h-[3px] bg-emerald-500" />
                      <div className="absolute top-1.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-emerald-500 text-slate-950 text-[9px] font-bold tracking-widest shadow-md whitespace-nowrap">AI-NATIVE</div>
                    </>
                  )}
                  <div className={`text-[11px] font-bold uppercase tracking-wider mb-1 ${c.id === 'cartie' ? 'text-emerald-300' : 'text-slate-500'}`}>{c.label}</div>
                  <div className={`text-3xl font-extrabold tabular-nums ${c.id === 'cartie' ? 'text-emerald-400' : 'text-slate-300'}`}>{standings[c.id].full}<span className="text-sm text-slate-500">/{totalRows}</span></div>
                  <div className="text-[10px] mt-1 text-slate-500">{standings[c.id].partial} partial · {standings[c.id].missing} miss</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Category filter chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="scorecard-filters">
          <button
            onClick={() => setActiveCat('all')}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${activeCat === 'all' ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20' : 'bg-white/5 border border-white/15 text-slate-300 hover:border-emerald-500 hover:text-white hover:bg-emerald-500/10'}`}
            data-testid="filter-all"
          >
            All categories ({totalRows})
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCat(cat.id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${activeCat === cat.id ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20' : 'bg-white/5 border border-white/15 text-slate-300 hover:border-emerald-500 hover:text-white hover:bg-emerald-500/10'}`}
              data-testid={`filter-${cat.id}`}
            >
              {cat.label} ({cat.rows.length})
            </button>
          ))}
        </div>
      </section>

      {/* Scorecard tables */}
      <section className="px-6 pb-16 max-w-7xl mx-auto space-y-10">
        {visibleCats.map(cat => {
          const Icon = cat.icon;
          return (
            <div key={cat.id} className="rounded-2xl bg-slate-900/60 border border-white/10 overflow-hidden shadow-sm" data-testid={`category-${cat.id}`}>
              <div className="px-6 py-5 border-b border-white/10 bg-slate-900/40">
                <div className="flex items-start gap-3">
                  <div className="inline-flex w-10 h-10 rounded-lg bg-emerald-500 items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-slate-950" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-white">{cat.label}</h2>
                    <p className="text-sm text-slate-400 mt-0.5">{cat.blurb}</p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="bg-slate-900/40 border-b border-white/10">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold text-slate-300 sticky left-0 bg-slate-900/40 min-w-[240px]">Feature</th>
                      {COMPETITORS.map(c => (
                        <th key={c.id} className={`text-center px-2 py-3 text-[11px] font-semibold whitespace-nowrap ${c.id === 'cartie' ? 'text-emerald-300 bg-emerald-500/15' : 'text-slate-400'}`}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {cat.rows.map((row, i) => (
                      <tr key={i} className="hover:bg-slate-900/40 transition-colors group">
                        <td className="px-5 py-4 sticky left-0 bg-slate-900/60 group-hover:bg-slate-900/40">
                          <div>
                            {row.link ? (
                              <Link to={row.link} className="text-white hover:text-emerald-300 transition-colors inline-flex items-center gap-1.5 font-medium" data-testid={`scorecard-link-${cat.id}-${i}`}>
                                {row.feature}
                                <ExternalLink className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                              </Link>
                            ) : (
                              <span className="text-white font-medium">{row.feature}</span>
                            )}
                            {row.note && <div className="text-xs text-slate-500 mt-1 leading-relaxed max-w-[600px]">{row.note}</div>}
                          </div>
                        </td>
                        {COMPETITORS.map(c => (
                          <td key={c.id} className={`px-2 py-4 ${c.id === 'cartie' ? 'bg-emerald-500/5' : ''}`}>
                            <Cell score={row.scores[c.id] || 'missing'} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </section>

      {/* Honesty footer + legend */}
      <section className="px-6 pb-20 max-w-7xl mx-auto">
        <div className="rounded-2xl bg-emerald-500/[0.06] border border-emerald-500/20 p-8 mb-6">
          <h3 className="text-2xl font-bold mb-4 text-white">Why this page exists</h3>
          <p className="text-slate-400 leading-relaxed mb-3">
            Every other FinOps comparison page is written by sales. They pick the rows they win, hide the rows they lose, and pad with "Generally Available" claims that don't survive a curl.
          </p>
          <p className="text-slate-400 leading-relaxed mb-3">
            We listed 4 rows we don't have yet ("Honestly: What We Don't Have Yet" category) and put an ETA next to each. We don't believe in <code className="text-emerald-300 bg-slate-900/60 px-1 rounded">contact sales</code>. We don't believe in stripping competitor advantages off the matrix.
          </p>
          <p className="text-slate-400 leading-relaxed">
            Click any <Check className="w-4 h-4 inline text-emerald-300" /> in the matrix. If the live page doesn't load, the row gets demoted. <span className="text-white font-semibold">That's our integrity contract.</span>
          </p>
        </div>

        {/* Legend */}
        <div className="rounded-xl bg-slate-900/40 border border-slate-800 p-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-300" /><span className="text-slate-400">Native, shipping today</span></div>
            <div className="flex items-center gap-2"><CircleDot className="w-4 h-4 text-amber-300" /><span className="text-slate-400">Partial — narrow scope or add-on</span></div>
            <div className="flex items-center gap-2"><X className="w-4 h-4 text-rose-400/60" /><span className="text-slate-400">Not in product</span></div>
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-300/80" /><span className="text-slate-400">Q3 2026 — honest "not yet"</span></div>
          </div>
        </div>

        {/* Head-to-head deep dives */}
        <div className="mt-6 rounded-xl bg-slate-900/40 border border-slate-800 p-6" data-testid="scorecard-vs-links">
          <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-slate-400 mb-4">Want the full story on one rival? Head-to-head deep dives</h3>
          <div className="flex flex-wrap gap-2">
            {Object.values(VS_VENDORS).map(v => (
              <Link
                key={v.slug}
                to={`/vs/${v.slug}`}
                data-testid={`scorecard-vs-link-${v.slug}`}
                className="px-3.5 py-1.5 rounded-full text-sm bg-slate-800/70 border border-slate-700 text-slate-300 hover:border-emerald-500/50 hover:text-emerald-300 transition-colors"
              >
                vs {v.name}
              </Link>
            ))}
            <Link to="/vs" data-testid="scorecard-vs-link-index" className="px-3.5 py-1.5 rounded-full text-sm bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 transition-colors">
              All comparisons →
            </Link>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
          <Link to="/signup" data-testid="scorecard-cta-signup" className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-6 py-3 rounded-full transition-colors">
            <Sparkles className="w-4 h-4" />
            Start a 14-day pilot
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/roi-pledge" data-testid="scorecard-cta-pledge" className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-100 font-medium px-6 py-3 rounded-full transition-colors">
            Read the 3× ROI Pledge first
          </Link>
          <ScorecardShareButton />
        </div>
      </section>
    </div>
  );
}
